/* Cart eligibility only. The installed Merchant embed owns its SDK, branded button and checkout flow.
   Keep unsupported carts out of its reduced payload; this bridge leaves normal checkout controls unchanged. */
const pending = new Set();
const placements = new Set();
const positiveInteger = (value) => Number.isSafeInteger(value) && value > 0;
const validLine = (line) =>
  line &&
  typeof line.key === 'string' &&
  line.key.length > 0 &&
  positiveInteger(line.variant_id) &&
  positiveInteger(line.quantity);

function matchesCart(cart, lines) {
  if (!cart || !Array.isArray(cart.items) || !cart.items.length || cart.items.length !== lines.length) return false;
  if (!positiveInteger(cart.item_count) || new Set(cart.items.map((line) => line.key)).size !== lines.length)
    return false;
  for (const field of ['cart_level_discount_applications', 'discount_applications', 'discounts']) {
    if (cart[field] != null && (!Array.isArray(cart[field]) || cart[field].length)) return false;
  }
  if (cart.total_discount != null && (!Number.isFinite(cart.total_discount) || cart.total_discount !== 0)) return false;
  let count = 0;
  for (const line of cart.items) {
    if (
      !validLine(line) ||
      line.selling_plan != null ||
      line.selling_plan_allocation != null ||
      line.parent_relationship != null ||
      line.requires_components ||
      line.gift_card ||
      (line.final_line_price != null &&
        line.original_line_price != null &&
        line.final_line_price < line.original_line_price)
    )
      return false;
    if (line.item_components != null && (!Array.isArray(line.item_components) || line.item_components.length))
      return false;
    for (const field of ['discount_allocations', 'line_level_discount_allocations', 'discounts']) {
      if (line[field] != null && (!Array.isArray(line[field]) || line[field].length)) return false;
    }
    const rendered = lines.find((item) => item.key === line.key);
    if (!rendered || rendered.variant_id !== line.variant_id || rendered.quantity !== line.quantity) return false;
    count += line.quantity;
  }
  return count === cart.item_count;
}

class VippsCart extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    this.revision = (this.revision || 0) + 1;
    this.hidden = true;
    this.dataset.state = 'checking';
    this.slotElement = this.parentElement?.matches('[data-vipps-slot]') ? this.parentElement : null;
    placements.add(this);
    this.addEventListener('click', (event) => this.checkClick(event), { capture: true, signal: this.events.signal });
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => this.showReady());
    this.observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-hurtigkasse-bound', 'data-hurtigkasse-hidden'],
    });
    const start = () => {
      if (!this.isConnected || this.events.signal.aborted) return;
      // A hydrated clone copies the SDK's bound marker, but not its listeners. Start from the inert template.
      this.querySelectorAll('.hurtigkasse-button-wrapper').forEach((wrapper) => wrapper.remove());
      try {
        this.lines = JSON.parse(this.querySelector('[data-vipps-lines]')?.textContent || 'null');
      } catch {
        this.lines = null;
      }
      this.form = this.closest('form');
      this.form?.addEventListener(
        'input',
        (event) => {
          if (event.target.matches('[name="updates[]"]')) this.refresh();
        },
        { signal: this.events.signal },
      );
      this.refresh();
    };
    // Modules can upgrade server markup before the parser has reached its JSON/template children.
    if (this.querySelector('[data-vipps-lines]')) start();
    else document.addEventListener('DOMContentLoaded', start, { once: true, signal: this.events.signal });
  }

  disconnectedCallback() {
    placements.delete(this);
    this.events?.abort();
    this.observer?.disconnect();
    this.invalidate('blocked');
  }

  allowed() {
    const dialog = this.closest('dialog');
    return (
      this.isConnected &&
      !document.hidden &&
      (!dialog || dialog.open) &&
      !pending.size &&
      this.dataset.serverEligible === 'true' &&
      Array.isArray(this.lines) &&
      this.lines.length > 0 &&
      this.lines.every(validLine) &&
      new Set(this.lines.map((line) => line.key)).size === this.lines.length &&
      !Array.from(this.form?.querySelectorAll('[name="updates[]"]') || []).some(
        (input) => input.value !== input.defaultValue,
      )
    );
  }

  invalidate(state = 'checking') {
    this.revision = (this.revision || 0) + 1;
    this.request?.abort();
    this.request = null;
    clearTimeout(this.readyTimeout);
    this.verified = false;
    this.hidden = true;
    this.dataset.state = state;
    if (this.slotElement) this.slotElement.style.minHeight = state === 'blocked' ? '' : '44px';
  }

  showReady() {
    if (!this.verified || !this.allowed()) return;
    const wrapper = this.querySelector('.hurtigkasse-button-wrapper');
    if (wrapper?.dataset.hurtigkasseHidden === 'true') return this.invalidate('blocked');
    const button = wrapper?.querySelector('.hurtigkasse-btn[data-hurtigkasse-bound="true"]');
    const ready = button?.querySelector('.hurtigkasse-btn__inner') && wrapper.dataset.hurtigkasseHidden !== 'true';
    this.hidden = !ready;
    this.dataset.state = ready ? 'ready' : 'checking';
    if (ready) {
      clearTimeout(this.readyTimeout);
      if (this.slotElement) this.slotElement.style.minHeight = '44px';
    }
  }

  async refresh(clickedButton) {
    this.invalidate(pending.size ? 'pending' : 'checking');
    if (!this.allowed()) {
      this.dataset.state = pending.size ? 'pending' : 'blocked';
      if (!pending.size && this.slotElement) this.slotElement.style.minHeight = '';
      return false;
    }
    const revision = this.revision;
    const request = new AbortController();
    this.request = request;
    const timeout = setTimeout(() => request.abort(), 5000);
    try {
      const url = new URL(this.dataset.cartUrl, location.origin);
      if (url.origin !== location.origin || !url.pathname.endsWith('/cart.js')) return false;
      const response = await fetch(url.href, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: request.signal,
      });
      const cart = response.ok ? await response.json() : null;
      if (request.signal.aborted || revision !== this.revision || !this.allowed() || !matchesCart(cart, this.lines))
        return false;
      if (!this.querySelector('.hurtigkasse-button-wrapper')) {
        const template = this.querySelector('template[data-vipps-button]');
        if (!template) return false;
        this.append(template.content.cloneNode(true));
        window.initHurtigkasseStorefront?.();
      }
      if (clickedButton && !this.contains(clickedButton)) return false;
      this.verified = true;
      this.readyTimeout = setTimeout(() => {
        if (revision === this.revision && this.hidden) this.invalidate('blocked');
      }, 5000);
      this.showReady();
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timeout);
      if (this.request === request) this.request = null;
      if (revision === this.revision && !this.verified) {
        this.dataset.state = 'blocked';
        if (this.slotElement) this.slotElement.style.minHeight = '';
      }
    }
  }

  async checkClick(event) {
    const button = event.target.closest('.hurtigkasse-btn');
    if (!button || this.replaying === button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
    button.disabled = true;
    const ready = await this.refresh(button);
    if (!this.isConnected || !this.contains(button)) return;
    button.disabled = false;
    if (!ready || this.dataset.state !== 'ready') return;
    // This one synchronous replay reaches the vendor's unchanged handler after the fresh read.
    this.replaying = button;
    button.click();
    this.replaying = null;
  }
}

if (!customElements.get('vipps-cart')) customElements.define('vipps-cart', VippsCart);

document.addEventListener('cart:pending', (event) => {
  const { id, pending: active } = event.detail || {};
  if (!id || typeof active !== 'boolean') return;
  if (active) pending.add(id);
  else pending.delete(id);
  for (const placement of placements) placement.refresh();
});
for (const type of ['cart:changed', 'panel:open']) {
  document.addEventListener(type, () => {
    for (const placement of placements) placement.refresh();
  });
}
document.addEventListener('visibilitychange', () => {
  for (const placement of placements) placement.refresh();
});
window.addEventListener('pageshow', () => {
  for (const placement of placements) placement.refresh();
});
